import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, EnvelopeSimple, LockKey, Eye, EyeSlash,
  ArrowsClockwise, ShieldStar, Sparkle, Sun, Moon, Coffee, Heart,
  Sparkle as SparkleIcon,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Stunning, encouragement-focused login page for non-field staff:
 * Office, Manager, HR, Service Coordinator. Different vibe from /adentry
 * (which is intentionally restrictive/intimidating for admins).
 *
 * Features:
 *  - Time-aware greeting (morning / afternoon / evening) with matching gradient
 *  - Rotating motivational quotes (encouragement)
 *  - Floating glass card layout with gradient mesh background
 *  - Confetti-style ambient particles
 *  - 2-step flow (creds → 8-digit OTP) like /adentry but with calm, warm UI
 *  - Auto-focuses next input, submits on Enter, big OTP boxes
 */

const QUOTES = [
  { text: "Small daily improvements are the key to staggering long-term results.", author: "Robin Sharma" },
  { text: "Service to others is the rent you pay for your room here on earth.", author: "Muhammad Ali" },
  { text: "The best way to find yourself is to lose yourself in the service of others.", author: "Mahatma Gandhi" },
  { text: "Care more than others think wise. Risk more than others think safe.", author: "Howard Schultz" },
  { text: "Hard work beats talent when talent doesn't work hard.", author: "Tim Notke" },
  { text: "What we do for ourselves dies with us. What we do for others remains.", author: "Albert Pike" },
  { text: "Discipline is the bridge between goals and accomplishment.", author: "Jim Rohn" },
  { text: "Your work is going to fill a large part of your life. Make it matter.", author: "Steve Jobs" },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return { text: "Working late?", icon: Moon, mood: "night" };
  if (h < 12) return { text: "Good morning", icon: Sun, mood: "morning" };
  if (h < 17) return { text: "Good afternoon", icon: Coffee, mood: "afternoon" };
  if (h < 21) return { text: "Good evening", icon: Sun, mood: "evening" };
  return { text: "Burning the midnight oil?", icon: Moon, mood: "night" };
}

const MOOD_PALETTE = {
  morning:   { from: "#FFE5B4", via: "#FFD1A1", to: "#F5A98F", accent: "#E07A5F" },
  afternoon: { from: "#FDF6E3", via: "#F4E4C1", to: "#D4B996", accent: "#C25D38" },
  evening:   { from: "#F5C9C9", via: "#E89A98", to: "#7E5C7C", accent: "#A05E80" },
  night:     { from: "#1B2545", via: "#264653", to: "#1A1A1A", accent: "#5C9F4F" },
};

export default function WorkLogin() {
  const { setUserAfterLogin } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("creds");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [ttl, setTtl] = useState(0);
  const [resendBusy, setResendBusy] = useState(false);
  const [quoteIdx, setQuoteIdx] = useState(0);

  const greeting = useMemo(() => getGreeting(), []);
  const palette = MOOD_PALETTE[greeting.mood];
  const isNight = greeting.mood === "night";
  const Icon = greeting.icon;

  // Cycle quotes every 8 seconds
  useEffect(() => {
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % QUOTES.length), 8000);
    return () => clearInterval(t);
  }, []);

  // Countdown for OTP TTL
  useEffect(() => {
    if (step !== "otp" || ttl <= 0) return;
    const t = setInterval(() => setTtl((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, ttl]);

  const submitCreds = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data.requires_otp) {
        setChallengeId(r.data.challenge_id);
        setOtpMessage(r.data.message || `A verification code was sent to ${r.data.email}.`);
        setTtl(r.data.ttl_seconds || 600);
        setOtp("");
        setStep("otp");
      } else {
        localStorage.setItem("rescigo_token", r.data.access_token);
        localStorage.setItem("rescigo_user", JSON.stringify(r.data.user));
        setUserAfterLogin?.(r.data.user);
        nav(routeForRole(r.data.user?.role));
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    }
    setBusy(false);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/login/verify-otp", {
        challenge_id: challengeId, otp: otp.trim(),
      });
      localStorage.setItem("rescigo_token", r.data.access_token);
      localStorage.setItem("rescigo_user", JSON.stringify(r.data.user));
      setUserAfterLogin?.(r.data.user);
      nav(routeForRole(r.data.user?.role));
    } catch (ex) {
      setErr(formatApiError(ex));
    }
    setBusy(false);
  };

  const resendOtp = async () => {
    setErr(""); setResendBusy(true);
    try {
      const r = await api.post("/auth/login/resend-otp", {
        challenge_id: challengeId, otp: "",
      });
      setChallengeId(r.data.challenge_id);
      setTtl(r.data.ttl_seconds || 600);
      setOtp("");
      setOtpMessage("A new code was sent — check your inbox.");
    } catch (ex) {
      setErr(formatApiError(ex));
    }
    setResendBusy(false);
  };

  const ttlMm = String(Math.floor(ttl / 60)).padStart(2, "0");
  const ttlSs = String(ttl % 60).padStart(2, "0");

  return (
    <div className="relative min-h-screen overflow-hidden flex" data-testid="work-login">
      {/* Animated gradient mesh background */}
      <div
        className="absolute inset-0 transition-colors duration-1000"
        style={{ background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.via} 45%, ${palette.to} 100%)` }}
      />
      <FloatingShapes color={palette.accent} dark={isNight} />
      <NoiseTexture />

      {/* Left brand panel */}
      <div className="hidden lg:flex relative z-10 lg:w-[45%] p-12 xl:p-16 flex-col justify-between">
        <Link to="/" data-testid="work-back-home"
              className={`inline-flex items-center gap-2 text-xs font-medium ${isNight ? "text-white/70 hover:text-white" : "text-[#1A1A1A]/70 hover:text-[#1A1A1A]"}`}>
          <ArrowLeft size={12} /> back to website
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-md text-[10px] uppercase tracking-[0.22em] font-semibold mb-6 ${isNight ? "bg-white/10 text-white" : "bg-white/30 text-[#1A1A1A]"}`}>
            <Icon size={12} weight="fill" /> {greeting.text}
          </div>

          <h1 className={`font-heading text-5xl xl:text-7xl tracking-tight leading-[0.95] font-medium mb-6 ${isNight ? "text-white" : "text-[#1A1A1A]"}`}>
            Show up.<br />
            <span className="italic font-light opacity-80">Make it count.</span>
          </h1>
          <p className={`text-base xl:text-lg leading-relaxed max-w-md ${isNight ? "text-white/80" : "text-[#2D2B26]/85"}`}>
            Welcome to your workspace. Every shift you log, every task you close,
            every customer you help — keeps a city cleaner. Let's get to it.
          </p>

          {/* Rotating quote */}
          <div className={`mt-12 pt-8 border-t ${isNight ? "border-white/15" : "border-black/15"}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={quoteIdx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.6 }}
                className="flex items-start gap-3"
              >
                <SparkleIcon size={16} weight="fill" className={isNight ? "text-white/50 mt-0.5" : "text-[#2D5A27] mt-0.5"} />
                <div>
                  <p className={`text-sm italic leading-relaxed ${isNight ? "text-white/85" : "text-[#1A1A1A]/85"}`}>
                    "{QUOTES[quoteIdx].text}"
                  </p>
                  <p className={`text-[10px] uppercase tracking-[0.22em] mt-2 ${isNight ? "text-white/50" : "text-[#5C5A55]"}`}>
                    — {QUOTES[quoteIdx].author}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Bottom mood strip */}
        <div className={`flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] ${isNight ? "text-white/40" : "text-[#1A1A1A]/50"}`}>
          <Heart size={10} weight="fill" className={isNight ? "text-white/60" : "text-[#C25D38]"} />
          For the team that keeps the city moving
        </div>
      </div>

      {/* Right form panel — floating glass card */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          {/* Mobile greeting (hidden on lg) */}
          <div className="lg:hidden mb-6 text-center">
            <div className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] ${isNight ? "text-white/80" : "text-[#1A1A1A]/70"}`}>
              <Icon size={11} weight="fill" /> {greeting.text}
            </div>
          </div>

          <div className={`relative rounded-3xl p-8 md:p-10 shadow-2xl backdrop-blur-2xl ${isNight ? "bg-white/[0.07] border border-white/10" : "bg-white/60 border border-white/40"}`}>
            <div className="absolute -top-3 left-8 right-8 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-50" />

            <AnimatePresence mode="wait">
              {step === "creds" && (
                <motion.div
                  key="creds"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="text-center mb-8">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3 ${isNight ? "bg-white/10" : "bg-[#2D5A27]/10"}`}>
                      <Sparkle size={20} weight="fill" color={palette.accent} />
                    </div>
                    <h2 className={`font-heading text-2xl md:text-3xl tracking-tight font-medium mb-1 ${isNight ? "text-white" : "text-[#1A1A1A]"}`}>
                      Sign in to your workspace
                    </h2>
                    <p className={`text-sm ${isNight ? "text-white/70" : "text-[#5C5A55]"}`}>
                      Office · Managers · HR · Coordinators
                    </p>
                  </div>

                  <form onSubmit={submitCreds} className="space-y-5">
                    <Field
                      label="Work email" Icon={EnvelopeSimple}
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="firstname@yourdomain.com"
                      type="email" autoFocus testid="work-email" dark={isNight}
                    />
                    <Field
                      label="Password" Icon={LockKey}
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      type={showPwd ? "text" : "password"}
                      testid="work-password" dark={isNight}
                      trailing={
                        <button type="button" onClick={() => setShowPwd((v) => !v)}
                                className={`p-1.5 ${isNight ? "text-white/50 hover:text-white" : "text-[#5C5A55] hover:text-[#1A1A1A]"}`}
                                tabIndex={-1}>
                          {showPwd ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </button>
                      }
                    />
                    {err && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                                  className={`text-xs px-3 py-2 rounded-lg ${isNight ? "bg-red-500/15 border border-red-400/30 text-red-200" : "bg-[#C25D38]/10 border border-[#C25D38]/30 text-[#7A2E18]"}`}
                                  data-testid="work-error">
                        {err}
                      </motion.div>
                    )}
                    <motion.button
                      type="submit" disabled={busy} data-testid="work-login-submit"
                      whileTap={{ scale: 0.98 }}
                      className="relative w-full h-12 rounded-xl font-medium inline-flex items-center justify-center gap-2 overflow-hidden text-white shadow-lg transition disabled:opacity-60"
                      style={{ background: `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accent}dd 100%)` }}
                    >
                      <span className="relative z-10 inline-flex items-center gap-2">
                        {busy ? "Signing you in…" : "Continue"}
                        <ArrowRight size={14} weight="bold" />
                      </span>
                    </motion.button>

                    <div className={`text-[11px] text-center ${isNight ? "text-white/50" : "text-[#5C5A55]"}`}>
                      <ShieldStar size={11} className="inline mr-1" weight="fill" />
                      Two-step verification protects your account.
                    </div>
                  </form>
                </motion.div>
              )}

              {step === "otp" && (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="text-center mb-8">
                    <motion.div
                      initial={{ scale: 0 }} animate={{ scale: 1 }}
                      transition={{ delay: 0.1, type: "spring", stiffness: 260, damping: 18 }}
                      className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3 ${isNight ? "bg-white/10" : "bg-[#2D5A27]/10"}`}
                    >
                      <ShieldStar size={20} weight="fill" color={palette.accent} />
                    </motion.div>
                    <h2 className={`font-heading text-2xl md:text-3xl tracking-tight font-medium mb-1 ${isNight ? "text-white" : "text-[#1A1A1A]"}`}>
                      Almost there ✨
                    </h2>
                    <p className={`text-sm ${isNight ? "text-white/70" : "text-[#5C5A55]"}`} data-testid="work-otp-message">
                      {otpMessage}
                    </p>
                  </div>

                  <form onSubmit={submitOtp} className="space-y-5">
                    <div>
                      <label className={`text-[10px] uppercase tracking-[0.22em] font-medium block mb-2 ${isNight ? "text-white/60" : "text-[#5C5A55]"}`}>
                        8-digit verification code
                      </label>
                      <input
                        autoFocus type="text" inputMode="numeric" maxLength={8} pattern="[0-9]{8}"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                        placeholder="••••••••"
                        data-testid="work-otp-input"
                        className={`w-full h-14 px-4 rounded-xl text-center text-2xl font-mono tracking-[0.4em] outline-none transition ${isNight ? "bg-white/5 border border-white/15 focus:border-white/40 text-white placeholder:text-white/20" : "bg-white/70 border border-white/60 focus:border-[#2D5A27] focus:ring-2 focus:ring-[#2D5A27]/20 text-[#1A1A1A] placeholder:text-[#1A1A1A]/20"}`}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className={isNight ? "text-white/60" : "text-[#5C5A55]"} data-testid="work-otp-ttl">
                        {ttl > 0 ? <>Expires in <b>{ttlMm}:{ttlSs}</b></> : <span className="text-[#C25D38] font-medium">Code expired</span>}
                      </span>
                      <button type="button" onClick={resendOtp} disabled={resendBusy}
                              data-testid="work-otp-resend"
                              className={`inline-flex items-center gap-1 disabled:opacity-50 ${isNight ? "text-white/80 hover:text-white" : "text-[#2D5A27] hover:text-[#1A3317]"}`}>
                        <ArrowsClockwise size={11} />
                        {resendBusy ? "Resending…" : "Resend code"}
                      </button>
                    </div>
                    {err && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                                  className={`text-xs px-3 py-2 rounded-lg ${isNight ? "bg-red-500/15 border border-red-400/30 text-red-200" : "bg-[#C25D38]/10 border border-[#C25D38]/30 text-[#7A2E18]"}`}
                                  data-testid="work-otp-error">
                        {err}
                      </motion.div>
                    )}
                    <motion.button
                      type="submit" disabled={busy || otp.length !== 8} data-testid="work-otp-submit"
                      whileTap={{ scale: 0.98 }}
                      className="relative w-full h-12 rounded-xl font-medium inline-flex items-center justify-center gap-2 text-white shadow-lg transition disabled:opacity-50"
                      style={{ background: `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accent}dd 100%)` }}
                    >
                      {busy ? "Verifying…" : "Sign in"}
                      <ArrowRight size={14} weight="bold" />
                    </motion.button>
                    <button type="button" onClick={() => { setStep("creds"); setOtp(""); setErr(""); }}
                            data-testid="work-otp-back"
                            className={`w-full text-xs inline-flex items-center justify-center gap-1.5 pt-1 ${isNight ? "text-white/60 hover:text-white" : "text-[#5C5A55] hover:text-[#1A1A1A]"}`}>
                      <ArrowLeft size={11} /> use a different account
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer hint */}
          <div className={`mt-6 text-center text-[11px] ${isNight ? "text-white/50" : "text-[#1A1A1A]/55"}`}>
            Field staff?  <Link to="/staff-app/login" data-testid="work-to-field" className="underline underline-offset-4 hover:no-underline">Use the staff app instead</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function routeForRole(role) {
  if (role === "service_coordinator") return "/coordinator";
  if (role === "govt") return "/gov";
  return "/dashboard";
}

const Field = ({ label, Icon, trailing, testid, dark, ...props }) => (
  <div>
    <label className={`text-[10px] uppercase tracking-[0.22em] font-medium block mb-2 ${dark ? "text-white/60" : "text-[#5C5A55]"}`}>
      {label}
    </label>
    <div className="relative">
      <Icon size={14} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${dark ? "text-white/40" : "text-[#5C5A55]"}`} />
      <input
        data-testid={testid}
        className={`w-full h-12 pl-10 pr-10 rounded-xl text-sm outline-none transition ${dark ? "bg-white/5 border border-white/15 focus:border-white/40 text-white placeholder:text-white/30" : "bg-white/70 border border-white/60 focus:border-[#2D5A27] focus:ring-2 focus:ring-[#2D5A27]/20 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30"}`}
        required
        {...props}
      />
      {trailing && <div className="absolute right-2 top-1/2 -translate-y-1/2">{trailing}</div>}
    </div>
  </div>
);

// Animated floating organic shapes (encouragement / energy)
function FloatingShapes({ color, dark }) {
  const shapes = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    size: 180 + (i * 73) % 220,
    x: (i * 37) % 80,
    y: (i * 53) % 80,
    delay: i * 0.7,
    duration: 14 + (i * 3) % 8,
  })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {shapes.map((s, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: s.size, height: s.size,
            left: `${s.x}%`, top: `${s.y}%`,
            background: `radial-gradient(circle, ${color}${dark ? "55" : "33"} 0%, transparent 70%)`,
            filter: "blur(40px)",
          }}
          animate={{
            x: [0, 40, -30, 0],
            y: [0, -25, 35, 0],
            scale: [1, 1.15, 0.92, 1],
          }}
          transition={{
            duration: s.duration, delay: s.delay, repeat: Infinity, ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

function NoiseTexture() {
  return (
    <div className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
         style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")" }}
    />
  );
}
